import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "wut_cart_v1";

/**
 * Cart item shape:
 * {
 *   id: string,              // product.id or vault.id
 *   source: "product"|"vault",
 *   name: string,
 *   price: number,
 *   image_url: string,
 *   quantity: number,
 * }
 */

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadFromStorage);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore quota errors */
    }
  }, [items]);

  const api = useMemo(() => ({
    items,
    count: items.reduce((n, it) => n + (it.quantity || 1), 0),
    subtotal: items.reduce((s, it) => s + (it.price || 0) * (it.quantity || 1), 0),

    add(item) {
      setItems((prev) => {
        const existing = prev.find((it) => it.id === item.id);
        if (existing) {
          // Vault items are unique (1 per), do not increment
          if (item.source === "vault") return prev;
          return prev.map((it) =>
            it.id === item.id
              ? { ...it, quantity: (it.quantity || 1) + (item.quantity || 1) }
              : it
          );
        }
        return [...prev, { quantity: 1, ...item }];
      });
    },

    remove(id) {
      setItems((prev) => prev.filter((it) => it.id !== id));
    },

    setQuantity(id, qty) {
      const next = Math.max(1, parseInt(qty, 10) || 1);
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, quantity: next } : it))
      );
    },

    clear() {
      setItems([]);
    },

    has(id) {
      return items.some((it) => it.id === id);
    },
  }), [items]);

  return <CartContext.Provider value={api}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
